import { PartialTenantCustomizations } from "src/tenantCustomizations/TenantCustomizations.ts";
import { getSalvosTaskContextMenuComponents } from "src/tenantCustomizations/tenants/salvos/salvosTaskContextMenuComponents.tsx";
import i18n from "i18next";
import { SalvosCustomerOrderContextMenuComponents } from "src/tenantCustomizations/tenants/salvos/sales/SalvosCustomerOrderContextMenuComponents.tsx";
import { printSalvosCustomerOrderConfirmation } from "src/tenantCustomizations/tenants/salvos/sales/printSalvosCustomerOrderConfirmation.tsx";
import { getSalvosCustomerOrderDataGridCustomColumns } from "src/tenantCustomizations/tenants/salvos/sales/salvosCustomerOrderDataGridCustomColumns.tsx";
import { getCustomerOrderTenantStateSpcSamplingMethod } from "src/components/views/erp/spcSamplingMethods/customerOrderTenantStateSpcSamplingMethod.tsx";
import { getCompletedFromCustomerOrderTenantStateSpcSamplingMethod } from "src/components/views/erp/spcSamplingMethods/completedFromCustomerOrderTenantStateSpcSamplingMethod.tsx";
import { getSubProjectDurationSpcSamplingMethod } from "src/components/views/erp/spcSamplingMethods/subProjectDurationSpcSamplingMethod.tsx";
import { getProjectActivityDurationSpcSamplingMethod } from "src/components/views/erp/spcSamplingMethods/projectActivityDurationSpcSamplingMethod.tsx";
import { SalvosProjectCustomizations } from "src/tenantCustomizations/tenants/salvos/project/SalvosProjectCustomizations.tsx";
import { getSwitchedToCustomerOrderTenantStateSpcSamplingMethod } from "src/components/views/erp/spcSamplingMethods/switchedToCustomerOrderTenantStateSpcSamplingMethod.tsx";
import {
	getInventoryLevelEventQuantitySpcSamplingMethod,
	getInventoryLevelEventTotalWeightSpcSamplingMethod,
} from "src/components/views/erp/spcSamplingMethods/inventoryLevelEventSpcSamplingMethods.tsx";
import { getSalvosCustomerOfferSummaryExtraFields } from "src/tenantCustomizations/tenants/salvos/sales/salvosCustomerOfferSummaryExtraFields.tsx";
import { getSalvosCustomerOrderSummaryExtraFields } from "src/tenantCustomizations/tenants/salvos/sales/salvosCustomerOrderSummaryExtraFields.tsx";
import { SalvosSalesCommissionViewExtraActions } from "src/tenantCustomizations/tenants/salvos/sales/salesCommissions/SalvosSalesCommissionViewExtraActions.tsx";
import { defaultSpcSamplingMethods } from "src/tenantCustomizations/defaultTenantCustomizations.ts";
import { SalvosInventoryForecastCustomFilters } from "src/tenantCustomizations/tenants/salvos/production/SalvosInventoryForecastCustomFilters.tsx";
import { getSalvosShopOrderSchedulingCustomColumns } from "src/tenantCustomizations/tenants/salvos/salvosShopOrderSchedulingCustomColumns.tsx";
import { getSalvosDeliveryTransportsViewExtraTabs } from "src/tenantCustomizations/tenants/salvos/delivery/salvosDeliveryTransportsViewExtraTabs.tsx";
import { SalvosAfterCustomerOrderLineConfigurationCreatedView } from "src/tenantCustomizations/tenants/salvos/sales/SalvosAfterCustomerOrderLineConfigurationCreatedView.tsx";
import { salvosTransportPlanningDeliveryStatusRenderer } from "src/tenantCustomizations/tenants/salvos/delivery/salvosTransportPlanningDeliveryStatusRenderer.tsx";

export const salvosCustomizations: PartialTenantCustomizations = {
	localizationOverrides: {
		fi: {
			"customer_offer_form.note": "Lisätiedot tehtaalle",
			"customer_offer_form.additional_address": "Kiinteistötunnus",
		},
	},
	tenantConfig: {
		erp: {
			enabled: true,
			accountingIntegrationEnabled: true,
			useCustomerOrderTenantStates: true,
			customerOrderTenantStates: [
				{ key: "initial", label: "Alustava" },
				{ key: "checked", label: "Tarkistettu" },
				{ key: "confirmed", label: "Vahvistettu" },
				{ key: "first_phase_planning", label: "Alkupään suunnittelussa" },
				{ key: "awaiting_building_licence", label: "Odottaa rakennuslupaa" },
				{ key: "building_licence_received", label: "Rakennuslupa saatu" },
				{ key: "delivery_date_agreed", label: "Toimituspäivä sovittu" },
				{ key: "second_phase_planning", label: "Loppupään suunnittelussa" },
				{ key: "ready_for_manufacturing", label: "Tuotantokelpoinen" },
				{ key: "on_manufacturing", label: "Tuotannossa" },
				{ key: "ready_for_delivery", label: "Toimitusvalmis" },
				{ key: "delivered", label: "Toimitettu" },
				{ key: "completed", label: "Valmis" },
				{ key: "cancelled", label: "Peruttu" },
			],
			customerOrderTenantStateColumnLabel: i18n.t("salvos_state"),
			customerOrderCheckingEnabled: false,
			customerOrderFreezingEnabled: true,
			customerOrderCommentingEnabled: true,
			customerOfferContractPrintoutEnabled: true,
			customerOfferContractSigningEnabled: true,
			reclamationsEnabled: true,
			configuratorEnabled: true,
			inputPricesWithVatByDefault: true,
			billingPlanEnabledByDefault: true,
			defaultCustomerType: "CONSUMER",
			projectActivitySchedulingEnabled: true,
			surveysEnabled: true,
			documentCustomerSharingEnabled: true,
			customerOfferFormFieldsToHide: [
				"customerOfferRequestNo",
				"externalOrderId",
				"deliveryTermsDestination",
				"deliveryTermsId",
				"currencyId",
				"deliveryMethodId",
				"vatHandlingTypeId",
				"vatCodeId",
				"billingPlanEnabled",
				"inputPricesWithVat",
			],
			customerFormFieldsToHide: [
				"customerGroupId",
				"discountPercentage",
				"customerPriceGroupId",
				"deliveryTermsId",
				"deliveryTermsDestination",
				"externalId",
				"deliveryAddress.addressName",
				"deliveryAddress.transportRouteId",
				"invoiceAddress.addressName",
			],
			customerFormUseDefaultContact: true,
			transportPlanningEnabled: true,
			reclamationRoundPlanningAiEnabled: true,
			vectorEmbeddingsEnabled: true,
			customerOrderLineImportMethods: ["STANDARD_TABULAR"],
			shopOrderPartListPrintedEnabled: true,
		},
		tasks: {
			enabled: true,
		},
		documentsEnabled: true,
		workspacesEnabled: true,
	},
	taskContextMenuComponents: ({ task }) => getSalvosTaskContextMenuComponents(task),
	spcSamplingMethods: ({ tenantConfig }) => [
		...defaultSpcSamplingMethods,
		getInventoryLevelEventQuantitySpcSamplingMethod(),
		getInventoryLevelEventTotalWeightSpcSamplingMethod(),
		getCustomerOrderTenantStateSpcSamplingMethod(tenantConfig),
		getCompletedFromCustomerOrderTenantStateSpcSamplingMethod(tenantConfig),
		getSubProjectDurationSpcSamplingMethod(),
		getProjectActivityDurationSpcSamplingMethod(),
		getSwitchedToCustomerOrderTenantStateSpcSamplingMethod(tenantConfig),
	],
	erp: {
		project: {
			subProjectContextMenuComponents: SalvosProjectCustomizations.SubProjectContextMenuComponents,
			createSubProjectFormComponents: SalvosProjectCustomizations.CreateSubProjectFormComponents,
			projectActivityJobQueue: {
				extraColumns: SalvosProjectCustomizations.projectActivityJobQueueExtraColumns,
			},
		},
		sales: {
			customerOrderContextMenuComponents: SalvosCustomerOrderContextMenuComponents,
			printCustomerOrderConfirmation: printSalvosCustomerOrderConfirmation,
			customerOrderDataGridCustomColumns: getSalvosCustomerOrderDataGridCustomColumns,
			customerOrderSummaryExtraFields: getSalvosCustomerOrderSummaryExtraFields,
			customerOfferSummaryExtraFields: getSalvosCustomerOfferSummaryExtraFields,
			salesCommissionViewExtraActions: SalvosSalesCommissionViewExtraActions,
			afterCustomerOrderLineConfigurationCreatedView: {
				title: i18n.t("order_change"),
				component: SalvosAfterCustomerOrderLineConfigurationCreatedView,
			},
		},
		production: {
			inventoryForecastCustomFilters: SalvosInventoryForecastCustomFilters,
			shopOrderSchedulingCustomColumns: getSalvosShopOrderSchedulingCustomColumns,
		},
		delivery: {
			deliveryTransportsViewExtraTabs: getSalvosDeliveryTransportsViewExtraTabs,
			transportPlanningDeliveryStatusRenderer: salvosTransportPlanningDeliveryStatusRenderer,
		},
	},
};
